import produce from 'immer';

import {BatchSchema} from 'constants/SampleConstants';
import {
  LastSyncedUpstreamValue,
  PrimarySample,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableRowUpdaterModel,
} from 'notebook/structured-tables/StructuredTableConstants';
import fetchAllSamples from 'notebook/structured-tables/util/fetchAllSamples';
import getLastSyncedUpstreamValueForColumn from 'notebook/structured-tables/util/getLastSyncedUpstreamValueForColumn';
import {resolveRowDelegateColumn} from 'notebook/structured-tables/util/resolveRowDelegateColumn';
import {cellHasUnsubmittedChanges} from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {omit} from 'util/omitKey';

// Keep in sync with LAST_SYNCED_UPSTREAM_VALUES_SCHEMA in benchling/notebook/structured_tables/validation.py
// and SUPPORTED_COLUMN_TYPES in benchling/notebook/structured_tables/resync/constants.py
export const SUPPORTED_COLUMN_TYPES = [
  TableColumnType.TAG_VALUE,
  TableColumnType.ALIAS,
  TableColumnType.UNIT_SELECTOR,
  TableColumnType.UNIT_REFERENCE,
  TableColumnType.MEASUREMENT_AMOUNT,
  TableColumnType.INGREDIENT_CATALOG_ID,
  TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
  TableColumnType.EXISTING_BIOENTITY,
  TableColumnType.CONTAINERS,
  TableColumnType.INGREDIENT_NOTES,
  TableColumnType.PROCESS_OUTPUT,
  TableColumnType.PROCESS_TASK,
  TableColumnType.PROCESS_TASK_ASSIGNEE,
  TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
  TableColumnType.PROCESS_TASK_STATUS,
  TableColumnType.CUSTOM,
  TableColumnType.STUDIES,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
  TableColumnType.STRING,
  TableColumnType.OPTION_SELECTOR,
];

// Column types to preserve last synced upstream values on refresh. These are column types that have no
// external source of truth and will only be updated on table submission.
// On refresh, any unsubmitted updates to the cells will be wiped away.
// Keep in sync with COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES in benchling/notebook/structured_tables/resync/constants.py
export const COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES = [
  TableColumnType.CUSTOM,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
  TableColumnType.OPTION_SELECTOR,
];

/**
 * Calculate and insert the appropriate lastSyncedUpstreamValues for the updatedRow.  If the value
 * for a column hasn't changed, then keep the old submittedAt time.
 *
 * @param columns - the columns in the table
 * @param originalRow - the originalRow with the old values of lastSyncedUpstreamValue
 * @param updatedRow - the new row which does not yet have lastSyncedUpstreamValues populated
 * @param lsuvSourceRow - a stand-in row holding the live values to snapshot, for a row updater that
 * deliberately left cells alone (e.g. an archived result). New values are otherwise read off updatedRow's
 * cells: a cell the updater skipped would overwrite its LSUV with whatever the row already displayed, and a
 * formula cell -- which holds its formula in `text` and has no typedValue -- would clear its LSUV entirely.
 */
// Keep in sync with update_last_synced_upstream_values in benchling/notebook/structured_tables/managers/submission_manager.py
export function updateLastSyncedUpstreamValues<TRowMetadata>(
  columns: Array<StructuredTableColumn>,
  originalRow: StructuredTableRow<TRowMetadata> | null,
  updatedRow: StructuredTableRow<TRowMetadata>,
  lsuvSourceRow?: StructuredTableRow<TRowMetadata> | null
): StructuredTableRow<TRowMetadata> {
  const lastSyncedUpstreamValues = originalRow?.lastSyncedUpstreamValues ?? [];
  const updatedSubmittedAt = new Date().toISOString();
  const valueSourceRow = lsuvSourceRow ?? updatedRow;
  const updatedLastSyncedUpstreamValues: Array<LastSyncedUpstreamValue> = columns
    .map((column, columnIndex) => {
      if (!SUPPORTED_COLUMN_TYPES.includes(column.columnType)) {
        return null;
      }
      const originalLastSyncedValue = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, column);
      const updatedTypedValue = valueSourceRow.cells[columnIndex].typedValue ?? null;
      const originalTypedValue = originalLastSyncedValue?.value ?? null;
      if (originalRow != null && areTypedValuesEquivalent(originalTypedValue, updatedTypedValue)) {
        return originalLastSyncedValue;
      } else if (
        originalRow != null &&
        updatedTypedValue?.type === FieldType.TEXT &&
        originalRow.cells[columnIndex].link
      ) {
        // If the plaintext typed value has changed outside of the editor context, clear its link
        updatedRow.cells[columnIndex].link = null;
      }
      return {
        columnType: column.columnType,
        columnGroup: column.columnGroup ?? null,
        fieldId: column.schemaFieldApiId ?? null,
        columnProvenanceUuid: column.provenanceUuid,
        value: updatedTypedValue,
        submittedAt: updatedSubmittedAt,
      };
    })
    .filter((x): x is LastSyncedUpstreamValue => x != null);
  return {
    ...updatedRow,
    lastSyncedUpstreamValues: updatedLastSyncedUpstreamValues,
  };
}

export interface StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> {
  samplesByRowId: {[rowId: string]: Array<PrimarySample>};
  rowUpdaters: Array<StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  /** Used by refreshEntitiesInStructuredTableUsingRowUpdater for unsubmitted-change detection. */
  computedTable?: Array<Array<EvaluatedCell>>;
}

export interface RefreshRowsWithRowUpdaterResult<TRowMetadata> {
  rows: Array<StructuredTableRow<TRowMetadata>>;
  /**
   * rowIds for which at least one matched row updater actually ran with skipLSUVUpdate !== true (and
   * therefore may have changed cells/lastSyncedUpstreamValues). Rows NOT in this set had every
   * matched updater declare skipLSUVUpdate: true, so their cells/LSUVs are guaranteed untouched --
   * callers should rely on this set rather than diffing cells/lastSyncedUpstreamValues for
   * reference equality, since a skipLSUVUpdate !== true updater can legitimately return a row whose
   * cells/LSUVs happen to be unchanged (e.g. the upstream value didn't drift), which looks
   * identical to "no LSUV-updating updater ran" if you only compare references.
   */
  rowIdsWithLSUVUpdate: Set<string>;
}

/**
 * Fetches live upstream values per row. Does not normalize merged cells.
 *
 * Honors skipLSUVUpdate: true per the contract on StructuredTableRowUpdater.skipLSUVUpdate.
 */
async function refreshRowsWithRowUpdater<TTableMetadata, TRowMetadata>({
  samplesByRowId,
  rowUpdaters,
  structuredTable,
}: StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>): Promise<
  RefreshRowsWithRowUpdaterResult<TRowMetadata>
> {
  const rowIdsWithLSUVUpdate = new Set<string>();
  const rows = await Promise.all(
    structuredTable.rows.map(async (originalRow) => {
      const {rowId} = originalRow;
      if (rowId == null || samplesByRowId[rowId] == null) {
        return originalRow;
      }
      const updatedSamples = await fetchAllSamples(samplesByRowId[rowId], rowUpdaters);

      let updatedRow = originalRow;
      for (const {updates, columnGroup} of updatedSamples) {
        let newRow = updatedRow;
        let ranLSUVUpdatingRow = false;
        let lsuvSourceRow: StructuredTableRow<TRowMetadata> | null = null;
        for (const {rowUpdater, updatedModel} of updates) {
          const result = rowUpdater.updateRow(updatedModel, structuredTable.columns, newRow, columnGroup);
          if (rowUpdater.skipLSUVUpdate === true) {
            // Only the metadata portion of a skipLSUVUpdate: true updater's updateRow result is
            // honored; any cells/LSUVs it returns are discarded (see the skipLSUVUpdate contract).
            newRow = {...newRow, metadata: result.metadata};
            continue;
          }
          newRow = result;
          ranLSUVUpdatingRow = true;
          // An updater that left cells alone can supply the live values for the LSUV snapshot separately.
          // Chain from the stand-in built so far so each updater contributes its own columns.
          lsuvSourceRow =
            rowUpdater.getLsuvSourceRow?.(
              updatedModel,
              structuredTable.columns,
              lsuvSourceRow ?? newRow,
              columnGroup
            ) ?? lsuvSourceRow;
        }
        if (!ranLSUVUpdatingRow) {
          updatedRow = newRow;
          continue;
        }
        rowIdsWithLSUVUpdate.add(rowId);
        const resolvedColumns = structuredTable.columns.map((column) =>
          resolveRowDelegateColumn(column, newRow, structuredTable.delegatedColumns)
        );
        updatedRow = {
          ...updateLastSyncedUpstreamValues(resolvedColumns, updatedRow, newRow, lsuvSourceRow),
          lastSyncedArchivePurpose: newRow.lastSyncedArchivePurpose,
        };
      }
      return updatedRow;
    })
  );
  return {rows, rowIdsWithLSUVUpdate};
}

/**
 * Updates a StructuredTable by using the given StructuredTableRowUpdater to fetch any samples
 * that it knows how to fetch and then update the given row for that sample.  This function is called
 * during the RefreshTableData flow and allows various StructuredTableAdapters to specify how they
 * want to update entries that aren't just the standard Bioentities or Storables and their tag values.
 *
 * Agent keep in sync: resync_structured_table_rows in
 * benchling/notebook/structured_tables/action_functions/resync.py
 */
export async function updateStructuredTableUsingRowUpdater<TTableMetadata, TRowMetadata>(
  args: StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>
): Promise<StructuredTable<TTableMetadata, TRowMetadata>> {
  const {rows: updatedRows} = await refreshRowsWithRowUpdater(args);
  return {
    ...args.structuredTable,
    rows: normalizeMergeCellRanges(updatedRows),
  };
}

function mergeRangeHasConflictingCells<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  startRowIdx: number,
  startColIdx: number,
  rowSpan: number,
  colSpan: number,
  {onlyFinalizedRows}: {onlyFinalizedRows: boolean}
): boolean {
  const primaryCell = rows[startRowIdx].cells[startColIdx];
  for (let rowIdx = startRowIdx; rowIdx < startRowIdx + rowSpan; rowIdx++) {
    if (onlyFinalizedRows && !rows[rowIdx].isFinalized) {
      continue;
    }
    for (let colIdx = startColIdx; colIdx < startColIdx + colSpan; colIdx++) {
      if (rowIdx === startRowIdx && colIdx === startColIdx) {
        continue;
      }
      const cell = rows[rowIdx].cells[colIdx];
      if (
        cell.text !== primaryCell.text ||
        !areTypedValuesEquivalent(cell.typedValue ?? null, primaryCell.typedValue ?? null)
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Normalize the results of updating the table via the row updater with any merged cell ranges on the table.
 * The row updater will write live data to cells, ignoring merge cell ranges. This method inspects each merge
 * cell range and takes one of two actions based on whether data in each cell in the range matches the
 * "primary" (top left) cell...
 * 1. (Data matches): Replace non-primary cells with an empty cell
 * 2. (Data does not match): Remove the merge so that all cells will be visible
 *
 * When `liveRows` is passed (entities-only refresh), conflict detection uses each row's live upstream
 * cell in the range so merges break when entities diverge. Ranges whose primary cell holds a formula are
 * exempt: the formula (not any single row's entity) defines the value for every row in the range, so a
 * diverged row must be surfaced with a stale/unsubmitted indicator on the range rather than by
 * unmerging it.
 *
 * Note: This method is compatible with 2D merge ranges, even though we only support single-column ranges in
 * structured tables.
 */
// Keep in sync with normalize_merge_cell_ranges in benchling/notebook/structured_tables/managers/merged_cell_manager.py
export function normalizeMergeCellRanges<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  liveRows?: Array<StructuredTableRow<TRowMetadata>>
): Array<StructuredTableRow<TRowMetadata>> {
  const rowsForValueComparison = liveRows ?? rows;

  return produce(rows, (draftRows) => {
    draftRows.forEach((row, rowIdx) => {
      row.cells.forEach((cell, colIdx) => {
        if (cell.mergeRowSpan == null && cell.mergeColSpan == null) {
          return;
        }

        const rowSpan = cell.mergeRowSpan ?? 1;
        const colSpan = cell.mergeColSpan ?? 1;
        const rowsInRange = draftRows.slice(rowIdx, rowIdx + rowSpan);

        const hasArchivedRow = rowsInRange.some(
          (rangeRow) => rangeRow.isFinalized && rangeRow.lastSyncedArchivePurpose != null
        );
        // A formula on the primary cell owns the value for the whole range, so live values that diverge
        // per row are not a conflict to resolve by unmerging.
        const primaryCellIsPreservedFormula = liveRows != null && TableUtil.isCellFormula(cell);
        // Standard refresh ignores non-finalized rows; entities refresh compares every row's live value.
        const hasConflictingValues =
          !primaryCellIsPreservedFormula &&
          mergeRangeHasConflictingCells(rowsForValueComparison, rowIdx, colIdx, rowSpan, colSpan, {
            onlyFinalizedRows: liveRows == null,
          });

        if (hasArchivedRow || hasConflictingValues) {
          draftRows[rowIdx].cells[colIdx] = omit(
            draftRows[rowIdx].cells[colIdx],
            'mergeRowSpan',
            'mergeColSpan'
          );
          return;
        }

        for (let i = rowIdx; i < rowIdx + rowSpan; i++) {
          for (let j = colIdx; j < colIdx + colSpan; j++) {
            if (i !== rowIdx || j !== colIdx) {
              draftRows[i].cells[j] = {text: ''};
            }
          }
        }
      });
    });
  });
}

/**
 * For each column, the primary (top left) cell of the merge range that hides this row's cell, or null
 * when the cell is not a hidden merged cell. The primary cell of a range maps to null.
 */
type MergePrimaryCellsForRow = Array<Cell | null>;

/**
 * Resolve, for every cell of every row, the primary cell of the merge range that hides it.
 *
 * Merge ranges are declared by their primary cell's `mergeRowSpan`/`mergeColSpan`, so a hidden cell can
 * only be recognized by scanning the rows above and to its left.
 */
function getMergePrimaryCellsByRow<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>
): Array<MergePrimaryCellsForRow> {
  const mergePrimaryCellsByRow: Array<MergePrimaryCellsForRow> = rows.map((row) => row.cells.map(() => null));
  rows.forEach((row, rowIdx) => {
    row.cells.forEach((cell, colIdx) => {
      const rowSpan = cell.mergeRowSpan ?? 1;
      const colSpan = cell.mergeColSpan ?? 1;
      if (rowSpan === 1 && colSpan === 1) {
        return;
      }
      for (let i = rowIdx; i < Math.min(rowIdx + rowSpan, rows.length); i++) {
        for (let j = colIdx; j < Math.min(colIdx + colSpan, rows[i].cells.length); j++) {
          if (i !== rowIdx || j !== colIdx) {
            mergePrimaryCellsByRow[i][j] = cell;
          }
        }
      }
    });
  });
  return mergePrimaryCellsByRow;
}

/**
 * Seed every hidden cell of a non-formula merge range with its primary cell's value, so the row updater
 * starts each member row from the value the range asserts for it.
 *
 * A merged range (e.g. a single input) means every row in it shares the primary cell's value, but only the
 * primary cell stores it: hidden members are persisted blank. After a refresh a blank hidden cell is
 * therefore ambiguous. It looks the same whether the updater had no live value for that row and left the
 * cell alone (a templated value with no upstream source) or the row's entity was genuinely cleared. Starting
 * members from the shared value removes the ambiguity: an untouched member still equals the primary, a
 * cleared member becomes blank and differs, and a member with a different live value differs. That lets
 * normalizeMergeCellRanges decide by plain value comparison; it re-blanks the members it keeps merged.
 *
 * Only `text` and `typedValue` are seeded so the member keeps its own formatting. Formula ranges are skipped:
 * the formula on the primary defines the value for every row, and pickLiveOrPreservedCells keeps hidden
 * members of a formula range blank.
 */
// Keep in sync with seed_hidden_merged_cells_with_primary_value in
// benchling/notebook/structured_tables/resync/entities_only.py
function seedHiddenMergedCellsWithPrimaryValue<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  mergePrimaryCellsByRow: Array<MergePrimaryCellsForRow>
): Array<StructuredTableRow<TRowMetadata>> {
  return rows.map((row, rowIdx) => {
    const mergePrimaryCells = mergePrimaryCellsByRow[rowIdx];
    if (mergePrimaryCells.every((primaryCell) => primaryCell == null)) {
      return row;
    }
    return {
      ...row,
      cells: row.cells.map((cell, colIdx) => {
        const primaryCell = mergePrimaryCells[colIdx];
        if (primaryCell == null || TableUtil.isCellFormula(primaryCell)) {
          return cell;
        }
        return primaryCell.typedValue == null
          ? {...omit(cell, 'typedValue'), text: primaryCell.text}
          : {...cell, text: primaryCell.text, typedValue: primaryCell.typedValue};
      }),
    };
  });
}

function pickLiveOrPreservedCells<TRowMetadata>(
  resolvedColumns: Array<StructuredTableColumn>,
  originalRow: StructuredTableRow<TRowMetadata>,
  liveCells: Array<Cell>,
  computedRow: Array<EvaluatedCell>,
  schemas: Array<Schema | BatchSchema>,
  mergePrimaryCells: MergePrimaryCellsForRow
): Array<Cell> {
  return resolvedColumns.map((column, colIdx) => {
    const originalCell = originalRow.cells[colIdx];

    if (TableUtil.isCellFormula(originalCell)) {
      return originalCell;
    }

    // A hidden merged cell of a formula range (e.g. a single input holding a formula) has no value of its
    // own: the formula on the primary cell supplies the value for every row. Writing this row's live value
    // into it would make the range look like it has conflicting values and split the merge. Keeping the
    // cell blank leaves only its LSUV advancing, which surfaces the divergence as an unsubmitted change on
    // the range instead.
    const mergePrimaryCell = mergePrimaryCells[colIdx];
    if (mergePrimaryCell != null && TableUtil.isCellFormula(mergePrimaryCell)) {
      return originalCell;
    }

    if (!originalRow.isFinalized) {
      return liveCells[colIdx];
    }

    const lastSyncedUpstreamValues = originalRow.lastSyncedUpstreamValues;
    if (lastSyncedUpstreamValues === undefined) {
      return liveCells[colIdx];
    }
    // eslint-disable-next-line benchlint/no-eqeqeq-null -- null means the row was never synced; preserve edits
    if (lastSyncedUpstreamValues === null) {
      return originalCell;
    }

    if (cellHasUnsubmittedChanges(computedRow[colIdx], lastSyncedUpstreamValues, column, schemas)) {
      return originalCell;
    }

    return liveCells[colIdx];
  });
}

function updateLsuvsAfterEntitiesRefresh<TRowMetadata>(
  originalRow: StructuredTableRow<TRowMetadata>,
  refreshedRow: StructuredTableRow<TRowMetadata>,
  resolvedColumns: Array<StructuredTableColumn>,
  computedRow: Array<EvaluatedCell>
): Array<LastSyncedUpstreamValue> | null | undefined {
  if (originalRow.isFinalized) {
    return refreshedRow.lastSyncedUpstreamValues;
  }

  const originalLsuvs = originalRow.lastSyncedUpstreamValues ?? [];
  const refreshedLsuvs = refreshedRow.lastSyncedUpstreamValues ?? [];
  const updatedLsuvs = resolvedColumns
    .map((column, colIdx) => {
      const refreshedLSUV = getLastSyncedUpstreamValueForColumn(refreshedLsuvs, column);
      // We specifically preserve LSUVs for formula cells when the evaluated result differs from live.
      // We should keep the out-of-date indicators if a formula is still producing a different value from live.
      if (
        TableUtil.isCellFormula(originalRow.cells[colIdx]) &&
        !areTypedValuesEquivalent(refreshedLSUV?.value ?? null, computedRow[colIdx]?.value ?? null)
      ) {
        return getLastSyncedUpstreamValueForColumn(originalLsuvs, column);
      }
      return refreshedLSUV;
    })
    .filter((x): x is LastSyncedUpstreamValue => x != null);

  if (updatedLsuvs.length === 0) {
    return originalRow.lastSyncedUpstreamValues;
  }
  return updatedLsuvs;
}

/**
 * A partially non-destructive alternative to updateStructuredTableUsingRowUpdater.
 *
 * Runs the same row-updater + LSUV pipeline, then merges live values back onto the prior row on a
 * per-cell basis (preserving formulas and unsubmitted edits on submitted rows). Merged cells are
 * finalized last so each row's live upstream value decides whether the merge is kept: hidden members
 * start from the primary's value (see seedHiddenMergedCellsWithPrimaryValue), so a member the updater
 * has no live value for still agrees with the primary while a cleared or diverged member splits the
 * range. Ranges whose primary cell holds a formula keep their hidden cells blank and stay merged, so a
 * row whose entity diverged shows up as an unsubmitted change on the range instead of splitting it.
 *
 * On unsubmitted rows, every non-formula cell advances to its live value; formula cells and their
 * LSUVs are preserved when the evaluated result differs from live.
 *
 * On submitted rows, a cell advances only when it has no formula and no unsubmitted edit (same rules
 * as stale-cell indicators). LSUVs always reflect the live upstream state.
 */
export type EntitiesStructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> =
  StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> & {
    computedTable: Array<Array<EvaluatedCell>>;
  };

export async function refreshEntitiesInStructuredTableUsingRowUpdater<TTableMetadata, TRowMetadata>(
  args: EntitiesStructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>
): Promise<StructuredTable<TTableMetadata, TRowMetadata>> {
  const {samplesByRowId, structuredTable, computedTable, rowUpdaters} = args;
  const schemas = structuredTable.schemas ?? [];

  const mergePrimaryCellsByRow = getMergePrimaryCellsByRow(structuredTable.rows);

  const {rows: liveRows, rowIdsWithLSUVUpdate} = await refreshRowsWithRowUpdater({
    samplesByRowId,
    rowUpdaters,
    structuredTable: {
      ...structuredTable,
      rows: seedHiddenMergedCellsWithPrimaryValue(structuredTable.rows, mergePrimaryCellsByRow),
    },
  });

  const originalRowsByRowId = new Map(
    structuredTable.rows.flatMap((row) => (row.rowId != null ? [[row.rowId, row] as const] : []))
  );

  const rowsAfterCellPolicy = liveRows.map((liveRow, rowIndex) => {
    if (liveRow.rowId == null || samplesByRowId[liveRow.rowId] == null) {
      return liveRow;
    }
    const originalRow = originalRowsByRowId.get(liveRow.rowId);
    if (originalRow == null) {
      return liveRow;
    }

    if (!rowIdsWithLSUVUpdate.has(liveRow.rowId)) {
      return liveRow;
    }

    const resolvedColumns = structuredTable.columns.map((column) =>
      resolveRowDelegateColumn(column, liveRow, structuredTable.delegatedColumns)
    );

    return {
      ...liveRow,
      cells: pickLiveOrPreservedCells(
        resolvedColumns,
        originalRow,
        liveRow.cells,
        computedTable[rowIndex],
        schemas,
        mergePrimaryCellsByRow[rowIndex]
      ),
      lastSyncedUpstreamValues: updateLsuvsAfterEntitiesRefresh(
        originalRow,
        liveRow,
        resolvedColumns,
        computedTable[rowIndex]
      ),
    };
  });

  return {
    ...structuredTable,
    rows: normalizeMergeCellRanges(rowsAfterCellPolicy, liveRows),
  };
}
